import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, XCircle } from 'lucide-react';
import api from '../../../utils/api';
import MainLayout from '../../../components/layout/MainLayout';
import { io } from 'socket.io-client';
import { useAuth } from '../../../context/AuthContext';

const WWebBulkJobsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();

    // Set up Socket.IO for real-time updates
    const socketUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';
    const socket = io(socketUrl);
    
    if (user?.tenantId) {
      socket.emit('join_tenant', user.tenantId);
    }

    socket.on('wa_web_bulk_progress', ({ jobId, progress }) => {
      setJobs(prev => prev.map(job => 
        job._id === jobId ? { ...job, progress } : job
      ));
    });

    socket.on('wa_web_bulk_complete', ({ jobId }) => {
      setJobs(prev => prev.map(job => 
        job._id === jobId ? { ...job, status: 'completed' } : job
      ));
    });

    socket.on('wa_web_bulk_paused', ({ jobId }) => {
      setJobs(prev => prev.map(job => 
        job._id === jobId ? { ...job, status: 'paused' } : job
      ));
    });

    socket.on('wa_web_bulk_cancelled', ({ jobId }) => {
        setJobs(prev => prev.map(job => 
          job._id === jobId ? { ...job, status: 'cancelled' } : job
        ));
      });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await api('/wa-web-automation/jobs');
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (jobId, action) => {
    try {
      const res = await api(`/wa-web-automation/jobs/${jobId}/${action}`, { method: 'PATCH' });
      if (res.ok) {
        fetchJobs(); // Refresh immediately
      } else {
        const data = await res.json();
        alert(data.message || `Failed to ${action} job`);
      }
    } catch (err) {
      alert(`Error trying to ${action} job`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'running': return <span style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>Running</span>;
      case 'paused': return <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>Paused</span>;
      case 'completed': return <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>Completed</span>;
      case 'cancelled': return <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>Cancelled</span>;
      case 'failed': return <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>Failed</span>;
      default: return <span style={{ background: '#f3f4f6', color: '#374151', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>Pending</span>;
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <button 
            onClick={() => navigate('/automation')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '8px' }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>Campaign History</h1>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading campaigns...</div>
        ) : jobs.length === 0 ? (
          <div style={{ background: 'white', padding: '40px', textAlign: 'center', borderRadius: '12px', border: '1px solid var(--color-border-primary)' }}>
            <p style={{ color: 'var(--color-text-secondary)' }}>No campaigns found.</p>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--color-border-primary)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-background-secondary)', borderBottom: '1px solid var(--color-border-primary)', textAlign: 'left' }}>
                  <th style={{ padding: '16px', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '13px' }}>Template / Name</th>
                  <th style={{ padding: '16px', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '13px' }}>Status</th>
                  <th style={{ padding: '16px', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '13px' }}>Progress</th>
                  <th style={{ padding: '16px', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '13px' }}>Date</th>
                  <th style={{ padding: '16px', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '13px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const progressPct = job.leadCount > 0 ? Math.round((job.progress.processed / job.leadCount) * 100) : 0;
                  
                  return (
                    <tr key={job._id} style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>{job.templateName}</div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>By {job.createdByName}</div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        {getStatusBadge(job.status)}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ flex: 1, height: '6px', background: 'var(--color-background-secondary)', borderRadius: '3px', minWidth: '100px' }}>
                            <div style={{ width: `${progressPct}%`, height: '100%', background: 'var(--color-brand-primary)', borderRadius: '3px', transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>{job.progress.processed} / {job.leadCount}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '11px', marginTop: '4px' }}>
                          <span style={{ color: '#166534' }}>{job.progress.successCount} sent</span>
                          <span style={{ color: '#991b1b' }}>{job.progress.failCount} failed</span>
                          <span style={{ color: '#92400e' }}>{job.progress.skippedCount} skipped</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                        {new Date(job.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <button 
                            onClick={() => navigate(`/automation/whatsapp-web/bulk/jobs/${job._id}`)}
                            style={{ padding: '6px 12px', background: 'white', border: '1px solid var(--color-border-primary)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                          >
                            Details
                          </button>
                          
                          {job.status === 'running' && (
                            <button onClick={() => handleAction(job._id, 'pause')} title="Pause" style={{ padding: '6px', background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                              <Pause size={16} />
                            </button>
                          )}
                          
                          {job.status === 'paused' && (
                            <button onClick={() => handleAction(job._id, 'resume')} title="Resume" style={{ padding: '6px', background: '#dcfce7', color: '#166534', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                              <Play size={16} />
                            </button>
                          )}
                          
                          {['pending', 'running', 'paused'].includes(job.status) && (
                            <button onClick={() => { if(window.confirm('Cancel this campaign?')) handleAction(job._id, 'cancel'); }} title="Cancel" style={{ padding: '6px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                              <XCircle size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default WWebBulkJobsList;
